import type { Component } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import {
  CalendarIcon,
  CarIcon,
  ChartGanttIcon,
  CheckCircleIcon,
  CreditCardIcon,
  HandCoinsIcon,
  HomeIcon,
  ListIcon,
  MapPinIcon,
  MonitorPlayIcon,
  PlusCircleIcon,
  ReceiptIcon,
  ScrollTextIcon,
  ShieldIcon,
  StarIcon,
  TicketIcon,
  TrendingUpIcon,
  UserPlusIcon,
  UsersIcon,
  VideoIcon,
  WormIcon,
  HandshakeIcon,
} from 'lucide-vue-next';

export type NavPermission = [action: string, subject: string];

export type HeaderbarNavItem = {
  labelKey: string;
  icon: Component;
  route?: RouteLocationRaw;
  can?: NavPermission;
  showUnlessCan?: NavPermission;
  emphasis?: 'primary';
};

export type HeaderbarNavGroup = {
  labelKey: string;
  items: HeaderbarNavItem[];
};

export const headerbarNavGroups: HeaderbarNavGroup[] = [
  {
    labelKey: 'groupOverview',
    items: [
      {
        labelKey: 'dashboard',
        route: { name: 'home' },
        icon: HomeIcon,
        can: ['ros', 'Dashboard'],
      },
    ],
  },
  {
    labelKey: 'groupBookings',
    items: [
      {
        labelKey: 'handover',
        route: { name: 'upcomingHandovers' },
        icon: CalendarIcon,
        can: ['handle', 'Booking'],
      },
      {
        labelKey: 'bookingPlan',
        route: { name: 'bookingPlan' },
        icon: ChartGanttIcon,
        can: ['list', 'Allocation'],
      },
      {
        labelKey: 'bookingList',
        route: { name: 'bookingList' },
        icon: ListIcon,
        can: ['list', 'Booking'],
      },
      {
        labelKey: 'createBooking',
        route: { name: 'bookingCreate' },
        icon: PlusCircleIcon,
        can: ['ros', 'CreateBooking'],
        emphasis: 'primary',
      },
      {
        labelKey: 'insuranceCases',
        route: { name: 'insuranceCasesList' },
        icon: ShieldIcon,
        can: ['manage', 'InsuranceCase'],
      },
    ],
  },
  {
    labelKey: 'groupFleet',
    items: [
      {
        labelKey: 'vehiclePoolManagement',
        route: { name: 'vehiclePool' },
        icon: CarIcon,
        can: ['ros', 'VehiclePool'],
      },
      {
        labelKey: 'vehicleTypes',
        route: { name: 'vehicleTypes' },
        icon: CarIcon,
        can: ['read', 'VehicleType'],
      },
    ],
  },
  {
    labelKey: 'groupFinance',
    items: [
      {
        labelKey: 'invoices',
        route: { name: 'invoiceList' },
        icon: ReceiptIcon,
        can: ['manage', 'Invoice'],
      },
      {
        labelKey: 'receivables',
        route: { name: 'receivables' },
        icon: HandCoinsIcon,
        can: ['ros', 'Receivables'],
      },
      {
        labelKey: 'coupons',
        route: { name: 'couponList' },
        icon: TicketIcon,
        can: ['read', 'Coupons'],
      },
    ],
  },
  {
    labelKey: 'groupSales',
    items: [
      {
        labelKey: 'websiteLeads',
        route: { name: 'leadsList' },
        icon: HandshakeIcon,
        can: ['manage', 'Lead'],
      },
    ],
  },
  {
    labelKey: 'groupPeople',
    items: [
      {
        labelKey: 'users',
        route: { name: 'userList' },
        icon: UsersIcon,
        can: ['manage', 'User'],
      },
      {
        labelKey: 'createUser',
        route: { name: 'user', params: { id: 'new' } },
        icon: UserPlusIcon,
        can: ['create', 'User'],
        showUnlessCan: ['manage', 'User'],
      },
      {
        labelKey: 'reviews',
        route: { name: 'reviews' },
        icon: StarIcon,
        can: ['list', 'Review'],
      },
      {
        labelKey: 'trainingVideos',
        route: { name: 'trainingVideos' },
        icon: MonitorPlayIcon,
        can: ['read', 'Video'],
      },
    ],
  },
  {
    labelKey: 'groupAdministration',
    items: [
      {
        labelKey: 'media',
        route: { name: 'media' },
        icon: VideoIcon,
        can: ['read', 'Media'],
      },
      {
        labelKey: 'stations',
        route: { name: 'stationList' },
        icon: MapPinIcon,
        can: ['update', 'Station'],
      },
      {
        labelKey: 'yieldManagement',
        route: { name: 'yieldManagement' },
        icon: TrendingUpIcon,
        can: ['ros', 'YieldManagement'],
      },
      {
        labelKey: 'approvalRequests',
        route: { name: 'approvalRequests' },
        icon: CheckCircleIcon,
        can: ['manage', 'ApprovalRequest'],
      },
      {
        labelKey: 'infringements',
        route: { name: 'infringementsList' },
        icon: ScrollTextIcon,
        can: ['manage', 'Infringement'],
      },
      {
        labelKey: 'queueInspector',
        route: { name: 'queueInspector' },
        icon: WormIcon,
        can: ['manage', 'Queue'],
      },
      {
        labelKey: 'paymentTerminals',
        route: { name: 'paymentTerminalList' },
        icon: CreditCardIcon,
        can: ['manage', 'PaymentTerminal'],
      },
    ],
  },
];
